import { React, useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from 'styled-components';
import { Sparkles } from 'lucide-react';
import PaymentComponent from '../PaymentComponent';
import {
    Sheet,
    CloseWrap,
    Head,
    Chip,
    Title,
    Sub,
    EmptyState,
    PlanTierGrid,
    PlanTierGridShimmer,
    PlanTierTrustStrip,
    ActivationCodeEntryLink,
    planTierDialogPaperProps,
    getPlanTierDialogBackdropProps,
} from '../ui/planTierPresentation';
import ActivationCodeDialog from '../ui/ActivationCodeDialog';
import { fetchInstructorPaywallPackages } from '../services/instructorPlansPricingApi';
import {
  filterUpgradeablePlans,
  getRecommendedUpgradePlanId,
  isHighestTier,
  sortPlansByTier,
  tierRank,
} from '../services/instructorPlanTier';
import { formatPlanLabel, resolvePlanPricingDisplay } from '../admin/Packages/planDisplayUtils';
import { useAppContext } from '../../appprovider';

const SALES_EMAIL = 'contracts@studyak.ai';

export default function PackageDialog({ open, handleClose }) {
    const theme = useTheme();
    const { entitlements } = useAppContext();
    const [packages, setPackages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [buttonLoadingIndex, setButtonLoadingIndex] = useState(null);
    const [activationDialogOpen, setActivationDialogOpen] = useState(false);

    useEffect(() => {
        if (!open) {
            return;
        }
        setLoading(true);
        fetchInstructorPaywallPackages()
            .then((data) => {
                setPackages(data);
                setLoading(false);
            })
            .catch((error) => {
                console.error('Error fetching plan pricing:', error);
                setPackages([]);
                setLoading(false);
            });
    }, [open]);

    async function handleUserPlan(pkg, index, selection = {}) {
        setButtonLoadingIndex(index);
        const pricing = resolvePlanPricingDisplay(pkg, {
            billingPeriod: selection.billingPeriod || 'monthly',
        });
        const name = formatPlanLabel(pkg.name || pkg.tier);
        const tier = pkg.tier || name;
        const email = localStorage.getItem('email');

        localStorage.setItem('studyak_plan_tier', tier);
        localStorage.setItem('packageIdplan', tier);
        localStorage.setItem('name', name);

        try {
            if (pricing.mode === 'contact') {
                const subject = encodeURIComponent(`StudyAK upgrade enquiry — ${name}`);
                window.location.href = `mailto:${SALES_EMAIL}?subject=${subject}`;
                return;
            }

            if (pricing.mode === 'free') {
                window.location.href = `mailto:${SALES_EMAIL}?subject=${encodeURIComponent(`StudyAK ${name} plan`)}`;
                return;
            }

            localStorage.setItem('packagePrice', String(pricing.checkoutPrice));
            const paymentComponent = new PaymentComponent();
            await paymentComponent.makePayment(tier, selection.billingPeriod || 'monthly');
        } finally {
            setButtonLoadingIndex(null);
        }
    }

    const currentTier = entitlements?.tier || localStorage.getItem('studyak_plan_tier') || null;
    const currentPlanName = formatPlanLabel(currentTier || 'your plan');

    const sortedPkgs = sortPlansByTier(
        packages.filter((pkg) => tierRank(pkg.tier) >= 0),
    );
    const upgradeablePkgs = filterUpgradeablePlans(packages, currentTier);
    const recommendedId = getRecommendedUpgradePlanId(currentTier, upgradeablePkgs);
    const atHighestTier = isHighestTier(currentTier);

    const getCtaLabel = (pkg, { pricingMode, isHighlighted }) => {
        const label = formatPlanLabel(pkg.name || pkg.tier);
        if (pricingMode === 'contact') {
            return 'Contact sales';
        }
        if (pricingMode === 'free') {
            return `Continue on ${label}`;
        }
        if (isHighlighted) {
            return `Upgrade to ${label}`;
        }
        return `Choose ${label}`;
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            aria-labelledby="upgrade-plan-dialog"
            maxWidth={false}
            PaperProps={planTierDialogPaperProps}
            slotProps={{ backdrop: getPlanTierDialogBackdropProps(theme) }}
        >
            <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
                <Sheet>
                    <CloseWrap>
                        <IconButton onClick={handleClose} aria-label="Close upgrade plan dialog" size="small">
                            <CloseIcon />
                        </IconButton>
                    </CloseWrap>

                    <Head>
                        <Chip>
                            <Sparkles size={12} />
                            {upgradeablePkgs.length > 0 ? 'Upgrade available' : 'Plan status'}
                        </Chip>
                        <Title>Upgrade your plan</Title>
                        <Sub>
                            {currentPlanName ? (
                                <>You&apos;re on <b>{currentPlanName}</b>. Pick a higher tier to unlock more courses, assessments, and learners.</>
                            ) : (
                                <>Pick a higher tier to unlock more courses, assessments, and learners.</>
                            )}
                        </Sub>
                    </Head>

                    <ActivationCodeEntryLink onClick={() => setActivationDialogOpen(true)} />

                    {loading ? (
                        <PlanTierGridShimmer count={5} />
                    ) : sortedPkgs.length === 0 ? (
                        <EmptyState>
                            {packages.length === 0
                                ? 'Unable to load plans. Please try again later.'
                                : atHighestTier
                                    ? "You're on the highest available plan. Contact support to explore custom options."
                                    : 'No plans available. Please contact support if you need help.'}
                        </EmptyState>
                    ) : (
                        <PlanTierGrid
                            packages={sortedPkgs}
                            recommendedId={recommendedId}
                            buttonLoadingIndex={buttonLoadingIndex}
                            onSelectPlan={handleUserPlan}
                            getCtaLabel={getCtaLabel}
                            badgeLabel="Best value"
                            currentTier={currentTier}
                        />
                    )}

                    <PlanTierTrustStrip />
                </Sheet>
            </DialogContent>

            <ActivationCodeDialog
                open={activationDialogOpen}
                onClose={() => setActivationDialogOpen(false)}
                onSuccess={() => {
                    setActivationDialogOpen(false);
                    handleClose();
                }}
            />
        </Dialog>
    );
}
